import React, { useState, useMemo, useEffect } from 'react';
import { usePermission } from '../../../src/utils/permissions';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Separator } from '../../ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import { Progress } from '../../ui/progress';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../ui/dropdown-menu';
import {
  Receipt,
  Plus,
  Search,
  Eye,
  Download,
  TrendingUp,
  DollarSign,
  AlertTriangle,
  CheckCircle,
  Calendar,
  Users,
  Building,
  Save,
  X,
  Edit,
  Upload,
  Trash2,
  Copy,
  Archive,
  MoreVertical,
  Paperclip,
  RefreshCw,
  FileText,
  Clock,
  Send,
  XCircle
} from 'lucide-react';
import { financialService, APInvoice, APInvoiceCreateData } from '../../../src/services/financial.service';
import { useAuth } from '../../../src/hooks/useAuth';
import { useAPInvoices } from '../../../src/hooks/queries/use-ap-invoices';
import { useProjects } from '../../../src/hooks/queries/use-projects';
import { useContracts } from '../../../src/hooks/queries/use-contracts';
import { useAppNav } from '../../../src/contexts/app-nav.context';
import { showSuccess, showError } from '../../../src/utils/notification.utils';
import { CURRENCIES, getCachedCurrency, formatCurrencyCompact } from '../../../src/utils/currency';
import { useOrgCurrency } from '../../../src/hooks/useOrgCurrency';

// Invoice Form Component
function InvoiceFormModal({ mode, invoice, trigger, onSuccess, orgId, open: controlledOpen, onOpenChange }: {
  mode: 'add' | 'edit' | 'view';
  invoice?: any;
  trigger?: React.ReactNode;
  onSuccess?: () => void;
  orgId?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const isReadOnly = mode === 'view';
  const [activeTab, setActiveTab] = useState('basic');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    invoiceNumber: invoice?.invoiceNumber || '',
    poNumber: invoice?.poNumber || '',
    vendor: invoice?.vendor || '',
    project: invoice?.project || '',
    contract: invoice?.contract || '',
    invoiceDate: invoice?.invoiceDate || new Date().toISOString().split('T')[0],
    dueDate: invoice?.dueDate || '',
    receivedDate: invoice?.receivedDate || new Date().toISOString().split('T')[0],
    description: invoice?.description || '',
    subtotal: invoice?.subtotal || '',
    taxAmount: invoice?.taxAmount || '',
    totalAmount: invoice?.totalAmount || '',
    currency: invoice?.currency || getCachedCurrency(),
    paymentTerms: invoice?.paymentTerms || '30',
    department: invoice?.department || '',
    costCenter: invoice?.costCenter || '',
    glCode: invoice?.glCode || '',
    approvalRequired: invoice?.approvalRequired || true,
    notes: invoice?.notes || '',
    lineItems: invoice?.lineItems || [
      { id: 1, description: 'Construction materials', quantity: 100, unitPrice: 25.50, amount: 2550, taxable: true, costCode: 'MAT001' },
      { id: 2, description: 'Equipment rental', quantity: 5, unitPrice: 150.00, amount: 750, taxable: true, costCode: 'EQP001' },
      { id: 3, description: 'Labor services', quantity: 40, unitPrice: 75.00, amount: 3000, taxable: false, costCode: 'LAB001' }
    ],
    approvalHistory: invoice?.approvalHistory || [
      { id: 1, approver: 'Project Manager', role: 'PM', status: 'Pending', date: '', comments: '', level: 1, required: true },
      { id: 2, approver: 'Finance Manager', role: 'Finance', status: 'Pending', date: '', comments: '', level: 2, required: true },
      { id: 3, approver: 'CFO', role: 'Executive', status: 'Pending', date: '', comments: '', level: 3, required: false }
    ],
    attachments: invoice?.attachments || [
      { id: 1, name: 'Invoice_INV-2024-001.pdf', type: 'Invoice', size: '2.1 MB', uploadDate: '2024-08-15', uploadedBy: 'Vendor Portal' },
      { id: 2, name: 'Purchase_Order.pdf', type: 'PO', size: '850 KB', uploadDate: '2024-08-10', uploadedBy: 'Procurement' },
      { id: 3, name: 'Delivery_Receipt.pdf', type: 'Receipt', size: '1.2 MB', uploadDate: '2024-08-14', uploadedBy: 'Site Manager' }
    ]
  });

  const [errors, setErrors] = useState<{[key: string]: string}>({});

  const vendors = [
    'BuildCorp Industries',
    'SteelWorks Manufacturing',
    'ElectroMech Solutions',
    'WeatherShield Roofing',
    'EarthMovers Inc',
    'Creative Architects Studio',
    'Safety First Equipment',
    'Metro Supplies Ltd'
  ];

  const projects = [
    'Residential Complex Alpha',
    'Office Building Beta',
    'Industrial Warehouse Gamma',
    'Shopping Center Delta',
    'Mixed-Use Development Epsilon'
  ];

  const contracts = [
    'CNT-2024-001 - Main Construction Contract',
    'CNT-2024-002 - MEP Systems Installation',
    'CNT-2024-003 - Structural Steel Fabrication',
    'CNT-2024-004 - Site Preparation & Excavation',
    'CNT-2024-005 - Architectural Design Services'
  ];

  const departments = [
    'Construction',
    'Engineering',
    'Procurement',
    'Project Management',
    'Finance',
    'Quality Control',
    'Safety'
  ];

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};
    
    if (!formData.invoiceNumber.trim()) {
      newErrors.invoiceNumber = 'Invoice number is required';
    }
    if (!formData.vendor) {
      newErrors.vendor = 'Vendor is required';
    }
    if (!formData.project) {
      newErrors.project = 'Project is required';
    }
    if (!formData.totalAmount || parseFloat(formData.totalAmount) <= 0) {
      newErrors.totalAmount = 'Valid total amount is required';
    }
    if (!formData.invoiceDate) {
      newErrors.invoiceDate = 'Invoice date is required';
    }
    if (!formData.dueDate) {
      newErrors.dueDate = 'Due date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      const payload: APInvoiceCreateData = {
        organization_id: orgId || '',
        invoice_number: formData.invoiceNumber,
        vendor_name: formData.vendor,
        vendor_address: '',
        po_number: formData.poNumber || undefined,
        invoice_date: formData.invoiceDate,
        due_date: formData.dueDate || undefined,
        received_date: formData.receivedDate || undefined,
        payment_terms: formData.paymentTerms ? `${formData.paymentTerms} days` : undefined,
        currency: formData.currency,
        subtotal: parseFloat(formData.subtotal) || 0,
        tax_amount: parseFloat(formData.taxAmount) || 0,
        total_amount: parseFloat(formData.totalAmount) || 0,
        description: formData.description || undefined,
        notes: formData.notes || undefined,
        department: formData.department || undefined,
        cost_center: formData.costCenter || undefined,
        gl_code: formData.glCode || undefined,
        approval_required: formData.approvalRequired,
        status: 'draft',
      };
      if (invoice?.vendor_invoice_id) {
        await financialService.updateAPInvoice(invoice.vendor_invoice_id, payload);
        showSuccess('Invoice updated successfully');
      } else {
        await financialService.createAPInvoice(payload);
        showSuccess('Invoice created successfully');
      }
      onSuccess?.();
    } catch {
      showError('Failed to save invoice');
    } finally {
      setIsSubmitting(false);
    }
  };

  const addLineItem = () => {
    const newId = Math.max(...formData.lineItems.map(l => l.id), 0) + 1;
    setFormData({
      ...formData,
      lineItems: [...formData.lineItems, {
        id: newId,
        description: '',
        quantity: 1,
        unitPrice: 0,
        amount: 0,
        taxable: true,
        costCode: ''
      }]
    });
  };

  const removeLineItem = (id: number) => {
    setFormData({
      ...formData,
      lineItems: formData.lineItems.filter(l => l.id !== id)
    });
  };

  const updateLineItem = (id: number, field: string, value: string | number | boolean) => {
    setFormData({
      ...formData,
      lineItems: formData.lineItems.map(l => {
        if (l.id === id) {
          const updated = { ...l, [field]: value };
          if (field === 'quantity' || field === 'unitPrice') {
            updated.amount = updated.quantity * updated.unitPrice;
          }
          return updated;
        }
        return l;
      })
    });
  };

  const updateApprovalHistory = (id: number, field: string, value: string | boolean) => {
    setFormData({
      ...formData,
      approvalHistory: formData.approvalHistory.map(approval => 
        approval.id === id ? { ...approval, [field]: value } : approval
      )
    });
  };

  const calculateTotals = () => {
    const subtotal = formData.lineItems.reduce((sum, item) => sum + item.amount, 0);
    const taxableAmount = formData.lineItems.filter(item => item.taxable).reduce((sum, item) => sum + item.amount, 0);
    const taxAmount = taxableAmount * 0.08; // 8% tax rate
    const total = subtotal + taxAmount;
    return { subtotal, taxAmount, total };
  };

  const { subtotal, taxAmount, total } = calculateTotals();

  return (
    <Dialog open={controlledOpen} onOpenChange={onOpenChange}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="sm:max-w-[1400px] max-h-[95vh] overflow-y-auto modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5" />
            Invoice (AP) - {mode === 'add' ? 'New' : mode === 'edit' ? 'Edit' : 'View'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'add' ? 'Create a new accounts payable invoice with line items and approval workflow' : 
             mode === 'edit' ? 'Update invoice information and approval status' : 
             'View detailed invoice information and approval history'}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="basic">Basic Info</TabsTrigger>
            <TabsTrigger value="lineitems">Line Items</TabsTrigger>
            <TabsTrigger value="approval">Approval Workflow</TabsTrigger>
            <TabsTrigger value="accounting">Accounting</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
          </TabsList>

          <TabsContent value="basic" className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Receipt className="h-5 w-5" />
                  Invoice Information
                </h3>
                
                <div className="space-y-2">
                  <Label htmlFor="invoice-number">Invoice Number *</Label>
                  <Input 
                    id="invoice-number" 
                    value={formData.invoiceNumber}
                    onChange={(e) => setFormData({...formData, invoiceNumber: e.target.value})}
                    placeholder="Enter invoice number"
                    readOnly={isReadOnly}
                  />
                  {errors.invoiceNumber && <p className="text-sm text-destructive">{errors.invoiceNumber}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="po-number">Purchase Order Number</Label>
                  <Input 
                    id="po-number" 
                    value={formData.poNumber}
                    onChange={(e) => setFormData({...formData, poNumber: e.target.value})}
                    placeholder="Enter PO number"
                    readOnly={isReadOnly}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="vendor">Vendor *</Label>
                  <Select value={formData.vendor} onValueChange={(value) => setFormData({...formData, vendor: value})} disabled={isReadOnly}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select vendor" />
                    </SelectTrigger>
                    <SelectContent>
                      {vendors.map(vendor => (
                        <SelectItem key={vendor} value={vendor}>{vendor}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.vendor && <p className="text-sm text-destructive">{errors.vendor}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="project">Project *</Label>
                  <Select value={formData.project} onValueChange={(value) => setFormData({...formData, project: value})} disabled={isReadOnly}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select project" />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.map(project => (
                        <SelectItem key={project} value={project}>{project}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.project && <p className="text-sm text-destructive">{errors.project}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contract">Contract</Label>
                  <Select value={formData.contract} onValueChange={(value) => setFormData({...formData, contract: value})} disabled={isReadOnly}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select contract" />
                    </SelectTrigger>
                    <SelectContent>
                      {contracts.map(contract => (
                        <SelectItem key={contract} value={contract}>{contract}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea 
                    id="description" 
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Enter invoice description"
                    rows={3}
                    readOnly={isReadOnly}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Dates & Amounts
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="invoice-date">Invoice Date *</Label>
                    <Input 
                      id="invoice-date" 
                      type="date"
                      value={formData.invoiceDate}
                      onChange={(e) => setFormData({...formData, invoiceDate: e.target.value})}
                      readOnly={isReadOnly}
                    />
                    {errors.invoiceDate && <p className="text-sm text-destructive">{errors.invoiceDate}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="due-date">Due Date *</Label>
                    <Input 
                      id="due-date" 
                      type="date"
                      value={formData.dueDate}
                      onChange={(e) => setFormData({...formData, dueDate: e.target.value})}
                      readOnly={isReadOnly}
                    />
                    {errors.dueDate && <p className="text-sm text-destructive">{errors.dueDate}</p>}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="received-date">Received Date</Label>
                  <Input 
                    id="received-date" 
                    type="date"
                    value={formData.receivedDate}
                    onChange={(e) => setFormData({...formData, receivedDate: e.target.value})}
                    readOnly={isReadOnly}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="payment-terms">Payment Terms (days)</Label>
                    <Input 
                      id="payment-terms" 
                      type="number"
                      value={formData.paymentTerms}
                      onChange={(e) => setFormData({...formData, paymentTerms: e.target.value})}
                      placeholder="30"
                      readOnly={isReadOnly}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="currency">Currency</Label>
                    <Select value={formData.currency} onValueChange={(value) => setFormData({...formData, currency: value})} disabled={isReadOnly}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CURRENCIES.map((c) => (
                          <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="p-4 bg-muted rounded-lg">
                  <h4 className="font-semibold mb-2">Amount Summary</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span>Subtotal:</span>
                      <span>${subtotal.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Tax (8%):</span>
                      <span>${taxAmount.toLocaleString()}</span>
                    </div>
                    <Separator />
                    <div className="flex justify-between font-semibold">
                      <span>Total Amount:</span>
                      <span>${total.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea 
                    id="notes" 
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder="Additional notes or comments"
                    rows={3}
                    readOnly={isReadOnly}
                  />
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="lineitems" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Invoice Line Items
              </h3>
              {!isReadOnly && (
                <Button onClick={addLineItem} size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Line Item
                </Button>
              )}
            </div>
            
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Description</TableHead>
                    <TableHead>Quantity</TableHead>
                    <TableHead>Unit Price</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Taxable</TableHead>
                    <TableHead>Cost Code</TableHead>
                    {!isReadOnly && <TableHead>Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {formData.lineItems.map(item => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Input 
                          value={item.description}
                          onChange={(e) => updateLineItem(item.id, 'description', e.target.value)}
                          placeholder="Enter description"
                          readOnly={isReadOnly}
                        />
                      </TableCell>
                      <TableCell>
                        <Input 
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateLineItem(item.id, 'quantity', parseFloat(e.target.value) || 0)}
                          placeholder="0"
                          readOnly={isReadOnly}
                        />
                      </TableCell>
                      <TableCell>
                        <Input 
                          type="number"
                          step="0.01"
                          value={item.unitPrice}
                          onChange={(e) => updateLineItem(item.id, 'unitPrice', parseFloat(e.target.value) || 0)}
                          placeholder="0.00"
                          readOnly={isReadOnly}
                        />
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">${item.amount.toLocaleString()}</span>
                      </TableCell>
                      <TableCell>
                        <Select 
                          value={item.taxable.toString()} 
                          onValueChange={(value) => updateLineItem(item.id, 'taxable', value === 'true')}
                          disabled={isReadOnly}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="true">Yes</SelectItem>
                            <SelectItem value="false">No</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Input 
                          value={item.costCode}
                          onChange={(e) => updateLineItem(item.id, 'costCode', e.target.value)}
                          placeholder="Cost code"
                          readOnly={isReadOnly}
                        />
                      </TableCell>
                      {!isReadOnly && (
                        <TableCell>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => removeLineItem(item.id)}
                            disabled={formData.lineItems.length === 1}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="approval" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                Approval Workflow
              </h3>
            </div>
            
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Level</TableHead>
                    <TableHead>Approver</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Required</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Comments</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {formData.approvalHistory.map(approval => (
                    <TableRow key={approval.id}>
                      <TableCell>
                        <Badge variant="outline">Level {approval.level}</Badge>
                      </TableCell>
                      <TableCell>
                        <Input 
                          value={approval.approver}
                          onChange={(e) => updateApprovalHistory(approval.id, 'approver', e.target.value)}
                          placeholder="Approver name"
                          readOnly={isReadOnly}
                        />
                      </TableCell>
                      <TableCell>
                        <Input 
                          value={approval.role}
                          onChange={(e) => updateApprovalHistory(approval.id, 'role', e.target.value)}
                          placeholder="Role"
                          readOnly={isReadOnly}
                        />
                      </TableCell>
                      <TableCell>
                        <Select 
                          value={approval.required.toString()} 
                          onValueChange={(value) => updateApprovalHistory(approval.id, 'required', value === 'true')}
                          disabled={isReadOnly}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="true">Required</SelectItem>
                            <SelectItem value="false">Optional</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Select 
                          value={approval.status} 
                          onValueChange={(value) => updateApprovalHistory(approval.id, 'status', value)}
                          disabled={isReadOnly}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Pending">Pending</SelectItem>
                            <SelectItem value="Approved">Approved</SelectItem>
                            <SelectItem value="Rejected">Rejected</SelectItem>
                            <SelectItem value="Under Review">Under Review</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Input 
                          type="date"
                          value={approval.date}
                          onChange={(e) => updateApprovalHistory(approval.id, 'date', e.target.value)}
                          readOnly={isReadOnly}
                        />
                      </TableCell>
                      <TableCell>
                        <Input 
                          value={approval.comments}
                          onChange={(e) => updateApprovalHistory(approval.id, 'comments', e.target.value)}
                          placeholder="Comments"
                          readOnly={isReadOnly}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="accounting" className="space-y-6">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Building className="h-5 w-5" />
              Accounting Information
            </h3>
            
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="department">Department</Label>
                  <Select value={formData.department} onValueChange={(value) => setFormData({...formData, department: value})} disabled={isReadOnly}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent>
                      {departments.map(dept => (
                        <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cost-center">Cost Center</Label>
                  <Input 
                    id="cost-center" 
                    value={formData.costCenter}
                    onChange={(e) => setFormData({...formData, costCenter: e.target.value})}
                    placeholder="Enter cost center code"
                    readOnly={isReadOnly}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="gl-code">General Ledger Code</Label>
                  <Input 
                    id="gl-code" 
                    value={formData.glCode}
                    onChange={(e) => setFormData({...formData, glCode: e.target.value})}
                    placeholder="Enter GL account code"
                    readOnly={isReadOnly}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div className="p-4 bg-muted rounded-lg">
                  <h4 className="font-semibold mb-2">Payment Information</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Invoice Total:</span>
                      <span className="font-medium">${total.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Payment Terms:</span>
                      <span>{formData.paymentTerms} days</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Currency:</span>
                      <span>{formData.currency}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Due Date:</span>
                      <span>{formData.dueDate}</span>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-semibold mb-2 text-blue-800">Tax Information</h4>
                  <div className="space-y-1 text-sm text-blue-700">
                    <div className="flex justify-between">
                      <span>Taxable Amount:</span>
                      <span>${formData.lineItems.filter(item => item.taxable).reduce((sum, item) => sum + item.amount, 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Tax Rate:</span>
                      <span>8%</span>
                    </div>
                    <div className="flex justify-between font-medium">
                      <span>Tax Amount:</span>
                      <span>${taxAmount.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="documents" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Paperclip className="h-5 w-5" />
                Supporting Documents
              </h3>
              {!isReadOnly && (
                <Button size="sm">
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Document
                </Button>
              )}
            </div>
            
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Document Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead>Upload Date</TableHead>
                    <TableHead>Uploaded By</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {formData.attachments.map(doc => (
                    <TableRow key={doc.id}>
                      <TableCell className="font-medium">{doc.name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{doc.type}</Badge>
                      </TableCell>
                      <TableCell>{doc.size}</TableCell>
                      <TableCell>{doc.uploadDate}</TableCell>
                      <TableCell>{doc.uploadedBy}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm">
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="outline" size="sm">
                            <Download className="h-4 w-4" />
                          </Button>
                          {!isReadOnly && (
                            <Button variant="outline" size="sm">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {formData.attachments.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <Paperclip className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No documents uploaded yet</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="gap-2">
          <Button variant="outline">
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          {!isReadOnly && (
            <>
              <Button variant="outline">
                <Copy className="h-4 w-4 mr-2" />
                Duplicate
              </Button>
              <Button onClick={handleSave} disabled={isSubmitting}>
                {isSubmitting ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {mode === 'add' ? 'Create Invoice' : 'Update Invoice'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function InvoicesAP() {
  const { canWrite, canApprove, canDelete } = usePermission();
  const orgCurrency = useOrgCurrency();
  const { orgId } = useAuth();
  const { navigate, routeFilters } = useAppNav();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [contractFilter, setContractFilter] = useState('all');
  const [viewingInvoice, setViewingInvoice] = useState<any | null>(null);
  const [editingInvoice, setEditingInvoice] = useState<any | null>(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const { data: rawInvoices = [], isLoading: loading, refetch } = useAPInvoices(orgId);
  const { data: rawProjects = [] } = useProjects(orgId);
  const { data: rawContracts = [] } = useContracts(orgId);
  const projects = rawProjects as any[];
  const contracts = rawContracts as any[];

  // Apply incoming route filters on first render
  useEffect(() => {
    if (routeFilters.contract_id) setContractFilter(routeFilters.contract_id);
  }, []); // intentional empty dep

  const invoices = (rawInvoices as APInvoice[]).map(i => ({
    id: i.vendor_invoice_id,
    invoiceNumber: i.invoice_number,
    poNumber: i.po_number || '',
    vendor: i.vendor_name,
    project: i.project_id || '',
    contract: i.contract_id || '',
    invoiceDate: i.invoice_date,
    dueDate: i.due_date || '',
    receivedDate: i.received_date || '',
    subtotal: i.subtotal,
    taxAmount: i.tax_amount,
    totalAmount: i.total_amount,
    currency: i.currency,
    paymentTerms: 30,
    status: i.status,
    approvalLevel: 1,
    department: i.department || '',
    costCenter: i.cost_center || '',
    description: i.description || '',
    daysOverdue: 0,
    documentsCount: 0,
    lastUpdated: i.updated_at,
    _raw: i,
  }));

  const getProjectName = (projectId: string) => projects.find((p: any) => p.project_id === projectId)?.project_name || projectId;
  const getContractName = (contractId: string) => {
    const c = contracts.find((c: any) => c.contract_id === contractId);
    return c ? (c.contract_name || c.contract_number || contractId) : contractId;
  };

  const handleSubmitInvoice = async (id: string) => {
    try {
      await financialService.submitAPInvoice(id);
      showSuccess('Invoice submitted for approval');
      refetch();
    } catch {
      showError('Failed to submit invoice');
    }
  };

  const handleApproveInvoice = async (id: string) => {
    try {
      await financialService.approveAPInvoice(id);
      showSuccess('Invoice approved');
      refetch();
    } catch {
      showError('Failed to approve invoice');
    }
  };

  const openRejectDialog = (id: string) => {
    setRejectingId(id);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const doRejectInvoice = async () => {
    if (!rejectingId || !rejectReason.trim()) return;
    try {
      await financialService.rejectAPInvoice(rejectingId, rejectReason.trim());
      showSuccess('Invoice rejected');
      setRejectDialogOpen(false);
      refetch();
    } catch {
      showError('Failed to reject invoice');
    }
  };

  const handleDeleteInvoice = async (id: string) => {
    try {
      await financialService.deleteAPInvoice(id);
      showSuccess('Invoice deleted');
      refetch();
    } catch {
      showError('Failed to delete invoice');
    }
  };

  const filteredInvoices = useMemo(() => {
    return invoices.filter(invoice => {
      const matchesSearch = invoice.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          invoice.vendor.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          invoice.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || invoice.status.toLowerCase().replace(/ /g, '') === statusFilter;
      const matchesVendor = vendorFilter === 'all' || invoice.vendor === vendorFilter;
      const matchesContract = contractFilter === 'all' || invoice.contract === contractFilter;

      return matchesSearch && matchesStatus && matchesVendor && matchesContract;
    });
  }, [searchTerm, statusFilter, vendorFilter, contractFilter, invoices]);

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase().replace(' ', '')) {
      case 'pendingapproval':
        return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">Pending Approval</Badge>;
      case 'approved':
        return <Badge className="bg-green-100 text-green-800 border-green-200">Approved</Badge>;
      case 'underreview':
        return <Badge className="bg-blue-100 text-blue-800 border-blue-200">Under Review</Badge>;
      case 'paid':
        return <Badge className="bg-gray-100 text-gray-800 border-gray-200">Paid</Badge>;
      case 'overdue':
        return <Badge className="bg-red-100 text-red-800 border-red-200">Overdue</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800 border-red-200">Rejected</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setVendorFilter('all');
    setContractFilter('all');
  };

  const getTotalStats = () => {
    const pending = invoices.filter(i => i.status === 'Pending' || i.status === 'Pending Approval').length;
    const totalAmount = invoices.reduce((sum, i) => sum + i.totalAmount, 0);
    const total = invoices.length;
    const overdue = invoices.filter(i => i.status === 'Overdue').length;
    return { pending, totalAmount, total, overdue };
  };

  const stats = getTotalStats();
  const vendors = [...new Set(invoices.map(i => i.vendor))];

  return (
    <div className="space-y-6">
      {/* Enhanced Header */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
        <div>
          <h2 className="text-3xl font-semibold tracking-tight">Invoices (Accounts Payable)</h2>
          <p className="text-muted-foreground">Manage vendor invoices, approval workflows, and payment processing</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canWrite('/invoices-ap') && (
            <InvoiceFormModal
              mode="add"
              orgId={orgId ?? ''}
              onSuccess={() => refetch()}
              trigger={
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  New Invoice
                </Button>
              }
            />
          )}
          <Button variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Enhanced Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4 text-yellow-600" />
              Pending Approval
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-700">{stats.pending}</div>
            <p className="text-xs text-yellow-600 mt-1">Awaiting approval</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-green-600" />
              Total Amount
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-700">{formatCurrencyCompact(stats.totalAmount, orgCurrency)}</div>
            <p className="text-xs text-green-600 mt-1">All pending invoices</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-blue-600" />
              Total Invoices
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-700">{stats.total}</div>
            <p className="text-xs text-blue-600 mt-1">Invoices on record</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              Overdue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-700">{stats.overdue}</div>
            <p className="text-xs text-red-600 mt-1">Past due date</p>
          </CardContent>
        </Card>
      </div>

      {/* Enhanced Invoices Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5" />
            Invoice Directory
          </CardTitle>
          <CardDescription>
            Comprehensive accounts payable management with approval workflows
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice Details</TableHead>
                  <TableHead>Vendor & Project</TableHead>
                  <TableHead>Financial Information</TableHead>
                  <TableHead>Approval Status</TableHead>
                  <TableHead>Dates</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredInvoices.map(invoice => (
                  <TableRow key={invoice.id} className="hover:bg-muted/50">
                    <TableCell>
                      <div className="space-y-1">
                        <p className="font-medium">{invoice.invoiceNumber}</p>
                        <p className="text-sm text-muted-foreground">PO: {invoice.poNumber}</p>
                        <p className="text-sm">{invoice.description}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Badge variant="outline" className="text-xs">{invoice.department}</Badge>
                          {invoice.daysOverdue > 0 && (
                            <Badge variant="destructive" className="text-xs">
                              {invoice.daysOverdue} days overdue
                            </Badge>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1 text-sm">
                        <div className="flex items-center gap-2">
                          <Building className="h-3 w-3" />
                          <span className="font-medium">{invoice.vendor}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Users className="h-3 w-3" />
                          <span>{invoice.project}</span>
                        </div>
                        <div className="text-muted-foreground text-xs">{invoice.contract}</div>
                        <div className="text-muted-foreground">Cost Center: {invoice.costCenter}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1 text-sm">
                        <div>
                          <span className="font-medium text-lg">${invoice.totalAmount.toLocaleString()}</span>
                          <div className="text-xs text-muted-foreground">
                            Subtotal: ${invoice.subtotal.toLocaleString()}
                          </div>
                          {invoice.taxAmount > 0 && (
                            <div className="text-xs text-muted-foreground">
                              Tax: ${invoice.taxAmount.toLocaleString()}
                            </div>
                          )}
                        </div>
                        <div className="text-xs">
                          Payment: {invoice.paymentTerms} days • {invoice.currency}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-2">
                        {getStatusBadge(invoice.status)}
                        <div className="text-xs text-muted-foreground">
                          Level {invoice.approvalLevel} of 3
                        </div>
                        <Progress value={(invoice.approvalLevel / 3) * 100} className="h-2" />
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1 text-sm">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-3 w-3" />
                          <span>Invoice: {invoice.invoiceDate}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-3 w-3" />
                          <span>Due: {invoice.dueDate}</span>
                        </div>
                        <div className="text-muted-foreground text-xs">
                          Received: {invoice.receivedDate}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {invoice.documentsCount} documents
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem onSelect={() => setViewingInvoice(invoice)}>
                            <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          {canWrite('/invoices-ap') && (
                            <DropdownMenuItem onSelect={() => setEditingInvoice(invoice)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit Invoice
                            </DropdownMenuItem>
                          )}
                          {invoice.status === 'approved' && (
                            <DropdownMenuItem onSelect={() => navigate('/payments', { ap_invoice_id: String(invoice.id) })}>
                              <DollarSign className="h-4 w-4 mr-2" />
                              Create Payment
                            </DropdownMenuItem>
                          )}
                          {canWrite('/invoices-ap') && invoice._raw && invoice.status === 'draft' && (
                            <DropdownMenuItem onSelect={() => handleSubmitInvoice(invoice._raw.vendor_invoice_id)}>
                              <Send className="h-4 w-4 mr-2" />
                              Submit for Approval
                            </DropdownMenuItem>
                          )}
                          {canApprove('/invoices-ap') && invoice._raw && invoice.status === 'submitted' && (
                            <DropdownMenuItem onSelect={() => handleApproveInvoice(invoice._raw.vendor_invoice_id)}>
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Approve
                            </DropdownMenuItem>
                          )}
                          {canApprove('/invoices-ap') && invoice._raw && invoice.status === 'submitted' && (
                            <DropdownMenuItem className="text-destructive" onSelect={() => openRejectDialog(invoice._raw.vendor_invoice_id)}>
                              <XCircle className="h-4 w-4 mr-2" />
                              Reject
                            </DropdownMenuItem>
                          )}
                          {canDelete('/invoices-ap') && invoice._raw && (
                            <DropdownMenuItem className="text-destructive" onSelect={() => handleDeleteInvoice(invoice._raw.vendor_invoice_id)}>
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {filteredInvoices.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <Receipt className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>{invoices.length === 0 ? 'No invoices yet. Create your first invoice.' : 'No invoices match your current filters.'}</p>
                {invoices.length > 0 && (
                  <Button variant="outline" size="sm" onClick={resetFilters} className="mt-2">
                    Clear Filters
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {viewingInvoice && (
        <InvoiceFormModal
          mode="view"
          invoice={viewingInvoice}
          open={true}
          onOpenChange={(o) => { if (!o) setViewingInvoice(null); }}
        />
      )}
      {editingInvoice && (
        <InvoiceFormModal
          mode="edit"
          invoice={editingInvoice}
          orgId={orgId ?? ''}
          onSuccess={() => { refetch(); setEditingInvoice(null); }}
          open={true}
          onOpenChange={(o) => { if (!o) setEditingInvoice(null); }}
        />
      )}

      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Invoice</DialogTitle>
            <DialogDescription>Provide a reason for rejection. This will be recorded and visible to the submitter.</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label htmlFor="reject-reason">Rejection Reason</Label>
            <Textarea
              id="reject-reason"
              className="mt-1"
              placeholder="e.g. Missing site photos, incorrect amounts..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={doRejectInvoice} disabled={!rejectReason.trim()}>
              Reject Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}